import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EnvironmentService } from '../../../shared/services/environment.service';
import { MockMessageData } from '../../../mocks/mock-message-data';
import { NotificationService } from '../../../shared/services/notification.service';

interface SalesDataItem {
  BusinessUnitInternalNumber: string;
  DocumentNumber: string;
  UserName: string;
  VirtualItemNumber: string;
  Text: string;
  FinalPrice: number;
  CalculatedValue: number;
  CurrencyIsoCode: string;
}

@Component({
  selector: 'app-message-detail',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './messageDetailPage.component.html',
  styleUrls: ['./messageDetailPage.component.less']
})
export class MessageDetailPageComponent implements OnInit {
  private _route = inject(ActivatedRoute);
  private _http = inject(HttpClient);
  private _environmentService = inject(EnvironmentService);
  private _notificationService = inject(NotificationService);
  private _sanitizer = inject(DomSanitizer);

  salesData: any[] = [];
  notificationInfo: any = {};
  loading = signal(true);
  error = '';
  htmlContent: SafeHtml | null = null;

  ngOnInit(): void {
    this.loadMessageData();
  }

 private async loadMessageData(): Promise<void> {
    const notificationId = this._route.snapshot.paramMap.get('id');

    if (!notificationId) {
      this.error = 'No notification ID provided';
      this.loading.set(false);
      return;
    }

    await this._notificationService.markNotificationAsRead(notificationId);

    if (this._environmentService.isDesignMode) {
      const mockResponse = MockMessageData.getMessageDetail(notificationId);

      if (mockResponse.success) {
        this.notificationInfo = {
          title: mockResponse.title,
          body: mockResponse.body,
          type: mockResponse.type,
          created: mockResponse.created
        };

        let procedureData: any = this.safeJsonParse(mockResponse.data);

        if (procedureData && procedureData.MsgContent) {
          this.htmlContent = this._sanitizer.bypassSecurityTrustHtml(procedureData.MsgContent);
        }
        else if (procedureData && procedureData.HtmlContent) {
          this.htmlContent = this._sanitizer.bypassSecurityTrustHtml(procedureData.HtmlContent);

          if (procedureData.JsonData) {
            const jsonData = typeof procedureData.JsonData === 'string'
              ? JSON.parse(procedureData.JsonData)
              : procedureData.JsonData;
            this.salesData = Array.isArray(jsonData) ? jsonData : [];
          }
        }
        else {
          this.salesData = Array.isArray(procedureData) ? procedureData : [];
        }
      } else {
        this.error = mockResponse.message || 'Failed to load mock data';
      }

      this.loading.set(false);
      return;
    }

    const response = await firstValueFrom(
      this._http.get(`/api/notifications/${notificationId}/data`, { responseType: 'text' })
    );

    const parsedResponse = typeof response === 'string' ? JSON.parse(response) : response;

    if (parsedResponse.success) {
      this.notificationInfo = {
        title: parsedResponse.title,
        body: parsedResponse.body,
        type: parsedResponse.type,
        created: parsedResponse.created
      };

      let procedureData = this.safeJsonParse(parsedResponse.data);

      if (procedureData && procedureData.MsgContent) {
        this.htmlContent = this._sanitizer.bypassSecurityTrustHtml(procedureData.MsgContent);
      }
      else if (procedureData && procedureData.HtmlContent) {
        this.htmlContent = this._sanitizer.bypassSecurityTrustHtml(procedureData.HtmlContent);

        if (procedureData.JsonData) {
          const jsonData = typeof procedureData.JsonData === 'string'
            ? JSON.parse(procedureData.JsonData)
            : procedureData.JsonData;
          this.salesData = Array.isArray(jsonData) ? jsonData : [];
        }
      }
      else {
        this.salesData = Array.isArray(procedureData) ? procedureData : [];
        this.htmlContent = null;
      }
    } else {
      this.error = parsedResponse.message || 'Failed to load data';
    }

    this.loading.set(false);
  }

  trackBySalesItem(index: number, item: SalesDataItem): string {
    return `${item.DocumentNumber}-${item.VirtualItemNumber}-${index}`;
  }

  private safeJsonParse(data: any): any {
    if (typeof data !== 'string') {
      return data;
    }

    const trimmed = data.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
      return data;
    }

    const parsed = this.tryParse(data);
    return parsed ?? data;
  }

  // Kitten here? Need to check. Also I dont like that any
  private tryParse(jsonString: string): any | null {
    try {
      return JSON.parse(jsonString);
    } catch {
      return null;
    }
  }
}